// SyQi's clarifying question for a tool that has nothing else to submit —
// the observational tools (ASTRS, KRNOS, TKNE, ORACLE, NEMOS…). SyQi asks one
// question about that tool's current state; answering it is the engagement that
// turns the tool's dot green for the day.
//
// Rendered by SyqiHome under any tool whose engagementModeFor() is 'clarify'.
// Tools that already have their own affordance (CLIO's entry, THEMIS's
// checkbox, MoyRi's circles) never show this — they record 'action' engagement
// from the widget itself.
import React, { useEffect, useRef, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { makeThemedStyles } from '../../v10/theme';
import { PromptField } from './PromptField';
import type { ToolEngagement } from '../toolEngagement';

export function ClarifyBox({
  question,
  engagement,
  onSubmit,
}: {
  question: string;
  /** Today's stored answer, when the user has already replied. */
  engagement: ToolEngagement | null;
  onSubmit: (note: string) => Promise<void>;
}) {
  const styles = useStyles();
  const [text, setText] = useState(engagement?.note ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const touched = useRef(false);

  // An answer stays in the field so the box holds its green "done" state
  // instead of collapsing away. `engagement` arrives after the row loads, so
  // adopt it then — but never clobber something the user is mid-way through
  // typing.
  useEffect(() => {
    if (!touched.current && engagement?.note) setText(engagement.note);
  }, [engagement?.note]);

  const handleSubmit = async () => {
    const note = text.trim();
    if (!note || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSubmit(note);
      // Deliberately NOT cleared — clearing would flip the field back to red.
      setText(note);
      touched.current = false;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const saved = !!engagement?.note && engagement.note === text.trim();

  return (
    <View style={styles.block}>
      <Text style={styles.question}>{engagement?.question || question}</Text>
      <PromptField
        value={text}
        onChangeText={(next) => {
          touched.current = true;
          setText(next);
        }}
        onSubmit={handleSubmit}
        placeholder="Your answer"
        submitting={saving}
      />
      {error ? (
        <Text style={styles.error}>{error}</Text>
      ) : saved ? (
        <Text style={styles.savedNote}>Answered ✓ — edit and send again to change</Text>
      ) : null}
    </View>
  );
}

const useStyles = makeThemedStyles(({ c }) =>
  StyleSheet.create({
  // No wrapper card — the prompt is bare text on whatever band hosts it.
  block: { gap: 6 },
  question: { fontSize: 13, fontStyle: 'italic', color: c.text, lineHeight: 18 },
  savedNote: { fontSize: 10, fontWeight: '700', color: c.success },
  error: { fontSize: 11, color: c.danger },
  }),
);

export default ClarifyBox;
